/**
 * Business-error model for the Forgot Password form.
 *
 * Discriminated union: the generic `network` / `api` transport failures (from
 * the app-wide `@/utils/infra-error` classifier) plus the form-specific
 * `validation` variant (field-level messages produced by `validateDraft`).
 *
 * Anti email-enumeration rule (a business rule, hence modelled here): whatever
 * the backend answers, the user always sees the same generic copy. Surfacing the
 * server message (e.g. "no account for this email") would let an attacker probe
 * which emails have accounts. `kind` / `status` are preserved for diagnostics
 * and tests; only the user-facing `message` is pinned.
 */
import { classifyInfraError, type InfraFailure } from '@/utils/infra-error';

export type ForgotPasswordError =
  | InfraFailure
  | { kind: 'validation'; fields: Record<string, string> };

/** The single user-facing failure copy (anti email-enumeration). */
export const FORGOT_PASSWORD_SUBMIT_ERROR = 'An error occurred. Please try again.';

/**
 * Map an infrastructure error into a domain `ForgotPasswordError`, always
 * carrying the generic message; see the anti-enumeration note above.
 */
export function toForgotPasswordError(e: unknown): ForgotPasswordError {
  const failure = classifyInfraError(e, FORGOT_PASSWORD_SUBMIT_ERROR);
  return { ...failure, message: FORGOT_PASSWORD_SUBMIT_ERROR };
}
